/**
 * MobileBottomNav — fixed bottom bar (mobile only) for the 5 core
 * authenticated surfaces. Auto-hidden on auth + onboarding routes,
 * marketing landings, and any logged-out state.
 *
 * Spacing: pages need bottom padding (pb-24) on mobile to avoid the
 * fixed bar overlapping the last row. Applied via the parent layout
 * in App.tsx through a body class once the user is signed in.
 */
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth/AuthProvider";
import { Compass, Heart, MessageCircle, Sparkles, User } from "lucide-react";

const ITEMS = [
  { to: "/discover", label: "Discover", Icon: Compass },
  { to: "/matches", label: "Matches", Icon: Heart },
  { to: "/messages", label: "Messages", Icon: MessageCircle },
  { to: "/raela", label: "RAELA", Icon: Sparkles },
  { to: "/account", label: "Account", Icon: User },
] as const;

const HIDE_PREFIXES = [
  "/auth",
  "/reset-password",
  "/onboarding",
  "/fryqnc",
  "/pulsynq",
  "/mindlynq",
];

const MobileBottomNav = () => {
  const { session } = useAuth();
  const { pathname } = useLocation();

  if (!session) return null;
  if (pathname === "/" || HIDE_PREFIXES.some((p) => pathname.startsWith(p))) {
    return null;
  }

  return (
    <nav
      aria-label="Primary"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className="grid grid-cols-5">
        {ITEMS.map(({ to, label, Icon }) => (
          <li key={to}>
            <NavLink
              to={to}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${
                  isActive
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={`h-5 w-5 ${isActive ? "" : "opacity-80"}`}
                    aria-hidden
                  />
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default MobileBottomNav;
